import { useEffect, useState } from 'react';
import type { Ideation, Project } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { EditableActivityPlan } from '@/features/plans/EditableActivityPlan';
import { PlanificationShopPanel } from '@/features/planification/PlanificationShopPanel';
import { useToast } from '@/features/ui/toast/useToast';

interface Props {
  projectId: string;
  refreshKey?: number;
}

export function PlanificationSynthesis({ projectId, refreshKey = 0 }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [project, setProject] = useState<Project | null>(null);
  const [ideation, setIdeation] = useState<Ideation | null>(null);
  const [view, setView] = useState<'none' | 'prep' | 'deploy' | 'shop'>('none');
  /** Replié par défaut pour laisser voir les modules en dessous */
  const [sectionOpen, setSectionOpen] = useState(false);

  useEffect(() => {
    if (!profile || !projectId) return;
    let cancelled = false;
    async function load() {
      const { data: p, error: pErr } = await kuunda
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .maybeSingle();
      if (cancelled) return;
      if (pErr) {
        toast.error(pErr.message);
        return;
      }
      setProject((p as Project) ?? null);
      if (!p?.ideation_id) {
        setIdeation(null);
        return;
      }
      const { data: idea } = await kuunda
        .from('ideations')
        .select('*')
        .eq('id', p.ideation_id)
        .maybeSingle();
      if (!cancelled) setIdeation((idea as Ideation) ?? null);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [profile, projectId, refreshKey, toast]);

  useEffect(() => {
    setView('none');
    setSectionOpen(false);
  }, [projectId]);

  if (!projectId) return null;

  const zone = ideation
    ? [ideation.city, ideation.country_code].filter(Boolean).join(', ')
    : project?.description ?? '—';
  const problem = (ideation?.problems ?? []).join(' · ') || '—';
  const solution = (ideation?.solutions ?? []).join(' · ') || ideation?.business_idea || '—';
  const titleHint = project?.title ? project.title : 'Aucun projet sélectionné';

  return (
    <section className={`u-card u-synth-section${sectionOpen ? ' is-open' : ' is-collapsed'}`}>
      <button
        type="button"
        className="u-synth-section-toggle"
        onClick={() => setSectionOpen((o) => !o)}
        aria-expanded={sectionOpen}
      >
        <span className="u-synth-section-toggle-main">
          <span className="u-synth-chevron" aria-hidden>
            {sectionOpen ? '▼' : '▶'}
          </span>
          <span>
            <strong>Synthèse planification & déploiement</strong>
            <span className="u-synth-section-count">{titleHint}</span>
          </span>
        </span>
        <span className="u-synth-section-hint">{sectionOpen ? 'Replier' : 'Déplier'}</span>
      </button>

      {sectionOpen ? (
        !project ? (
          <p className="muted u-synth-section-desc">
            Sélectionnez un projet modélisé (BMC + BP).
          </p>
        ) : (
          <>
            <p className="muted u-synth-section-desc">
              Fiche projet et accès rapide aux plans / MarketPlace. Repliez la section pour accéder
              aux modules ci-dessous.
            </p>
            <dl className="u-synth-dl u-planif-synth-dl">
              <div>
                <dt>ID projet</dt>
                <dd>{project.id}</dd>
              </div>
              <div>
                <dt>Intitulé</dt>
                <dd>{project.title}</dd>
              </div>
              <div>
                <dt>Domaine d’activité</dt>
                <dd>{ideation?.domain || '—'}</dd>
              </div>
              <div>
                <dt>Besoins / problème</dt>
                <dd>{problem}</dd>
              </div>
              <div>
                <dt>Solution entrepreneuriale</dt>
                <dd>{solution}</dd>
              </div>
              <div>
                <dt>Zone d’opération</dt>
                <dd>{zone}</dd>
              </div>
            </dl>

            <div className="u-synth-downloads">
              <button
                type="button"
                className={`btn ${view === 'prep' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setView(view === 'prep' ? 'none' : 'prep')}
              >
                Plan de préparation
              </button>
              <button
                type="button"
                className={`btn ${view === 'deploy' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setView(view === 'deploy' ? 'none' : 'deploy')}
              >
                Plan de déploiement
              </button>
              <button
                type="button"
                className={`btn ${view === 'shop' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setView(view === 'shop' ? 'none' : 'shop')}
              >
                ISBD MarketPlace
              </button>
            </div>

            {view === 'prep' ? (
              <div style={{ marginTop: '1rem' }}>
                <EditableActivityPlan projectId={projectId} kind="prep" />
              </div>
            ) : null}
            {view === 'deploy' ? (
              <div style={{ marginTop: '1rem' }}>
                <EditableActivityPlan projectId={projectId} kind="deploy" />
              </div>
            ) : null}
            {view === 'shop' ? (
              <div style={{ marginTop: '1rem' }}>
                <PlanificationShopPanel projectId={projectId} />
              </div>
            ) : null}
          </>
        )
      ) : (
        <p className="muted u-synth-collapsed-hint">
          Section repliée — dépliez pour la fiche projet, les plans et le MarketPlace.
        </p>
      )}
    </section>
  );
}
